import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { FormGroup, AbstractControl } from "@angular/forms";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { Command, LabelCommand } from "@tylertech/commands-module";
import { LgdFormControlFactory } from "@tylertech/lgd-reactive-forms";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import { KeyValue, TransferStockLocationModel } from "@tylertech/tyler-api-types";
import { ItemLocationLookupService } from "../../services";
import { HttpErrorResponse } from "@angular/common/http";
export interface InventoryTransferDialogData
{
    readonly itemId: string;
}
@Component({
    selector: "inventory-transfer-dialog-component",
    templateUrl: "./inventoryTransferDialog.tmpl.html",
    standalone: false
})
export class InventoryTransferDialogComponent implements OnInit, OnDestroy
{
    private readonly destructor$ = new Subject<void>();
    private readonly locationsSubject = new BehaviorSubject<Array<KeyValue>>([]);
    readonly formGroup: FormGroup;
    readonly commands: Array<Command>;
    private model: TransferStockLocationModel;
    constructor(
        private readonly dialogRef: MatDialogRef<InventoryTransferDialogComponent, boolean>,
        @Inject(MAT_DIALOG_DATA)
        private readonly data: InventoryTransferDialogData,
        private $commonDialogService: CommonDialogsService,
        private $itemLocationLookupService: ItemLocationLookupService
    )
    {
        this.commands = [
            new LabelCommand({
                label: "Cancel",
                action: () => this.cancel(),
                getEnabled: () => true,
            }),
            new LabelCommand({
                label: "Save & Close",
                action: () => this.save(),
                getEnabled: () => this.formGroup.valid,
            }),
        ];
        this.formGroup = new FormGroup({
            transferFrom: LgdFormControlFactory.select<KeyValue>(undefined, true, {
                displayValueFn: x => x.Value,
            }),
            transferTo: LgdFormControlFactory.select<KeyValue>(undefined, true, {
                displayValueFn: x => x.Value,
            }),
            quantity: LgdFormControlFactory.number(undefined, true, {
                min: 0,
            }),
        });
    }

    private get itemId(): string
    {
        return this.data.itemId;
    }

    get locationsObservable(): Observable<Array<KeyValue>>
    {
        return this.locationsSubject.asObservable();
    }

    private get transferFromControl(): AbstractControl
    {
        return this.formGroup.get("transferFrom");
    }

    private get transferToControl(): AbstractControl
    {
        return this.formGroup.get("transferTo");
    }

    private get quantityControl(): AbstractControl
    {
        return this.formGroup.get("quantity");
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        const self = this;
        this.$itemLocationLookupService.GetItemLocations(this.itemId).then((result: Array<KeyValue>) =>
        {
            this.locationsSubject.next(result);
        });
        this.model = {
            LocationFromID: undefined,
            LocationToID: undefined,
            ItemID: undefined,
            TransferQuantity: undefined,
        };
        subscribeToValueChanges(this.transferFromControl, (newValue: KeyValue) =>
        {
            this.model.LocationFromID = newValue.Key;
        });
        subscribeToValueChanges(this.transferToControl, (newValue: KeyValue) =>
        {
            this.model.LocationToID = newValue.Key;
        });
        subscribeToValueChanges(this.quantityControl, (newValue: number) =>
        {
            this.model.TransferQuantity = newValue;
        });
        function subscribeToValueChanges<T>(control: AbstractControl, subscriptionFn: (newValue: T) => void): void
        {
            control.valueChanges.pipe(takeUntil(self.destructor$)).subscribe(x =>
            {
                subscriptionFn(x);
            });
        }
    }

    cancel(): void
    {
        this.dialogRef.close();
    }

    errorOnSave(error: string): void
    {
        this.$commonDialogService.showAlertMessage(error, "Invalid Input");
    }

    save(): void
    {
        this.$itemLocationLookupService.SaveItemLocation(this.model).then(
            (result: boolean): void =>
            {
                if (result)
                {
                    this.dialogRef.close(true);
                } else
                {
                    this.errorOnSave("");
                }
            },
            (error: HttpErrorResponse): void =>
            {
                this.errorOnSave(error.error.Message);
            }
        );
    }
}
